import { type Dispatch, type SetStateAction, useMemo, useState } from "react"

import type { TEmptyCallback } from "@/shared/types/functions"

type TUseInteractionState = {
    isActionAffected: boolean
    __isFocused: boolean
    actions: {
        onMouseEnter: TEmptyCallback
        onMouseLeave: TEmptyCallback
        onFocus: TEmptyCallback
        onBlur: TEmptyCallback
        onMouseMove: TEmptyCallback
        onKeyDown: TEmptyCallback
        onTouchStart: TEmptyCallback
        onTouchEnd: TEmptyCallback
        onClick: TEmptyCallback
        __setter: Dispatch<SetStateAction<boolean>>
    }
}

function useInteractionState(): ReturnType<() => TUseInteractionState> {
    const [isActionAffected, setIsActionAffected]: [boolean, Dispatch<SetStateAction<boolean>>] =
        useState<boolean>(false)
    const [isFocused, setIsFocused]: [boolean, Dispatch<SetStateAction<boolean>>] = useState<boolean>(false)

    return useMemo(
        (): TUseInteractionState => ({
            isActionAffected: isActionAffected || isFocused,
            __isFocused: isFocused,
            actions: {
                onMouseEnter: (): void => setIsActionAffected(true),
                onMouseLeave: (): void => setIsActionAffected(false),
                onFocus: (): void => setIsFocused(true),
                onBlur: (): void => setIsFocused(false),
                onMouseMove: (): void => setIsActionAffected(true),
                onTouchStart: (): void => setIsActionAffected(true),
                onTouchEnd: (): void => setIsActionAffected(false),
                onKeyDown: (): void => setIsActionAffected((prev: boolean): boolean => !prev),
                onClick: (): void => (setIsActionAffected(false), setIsFocused(false)),
                __setter: setIsActionAffected
            }
        }),
        [isActionAffected, isFocused]
    )
}

export { useInteractionState }
export type { TUseInteractionState }
